import { EventEmitter, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { skipWhile, take, tap } from 'rxjs/operators';
import { ApiService } from 'src/app/services/api/api.service';
import { ToastService } from 'src/app/services/toast/toast.service';
import { Router } from '@angular/router';
import { AuthService } from 'src/app/services/auth/auth.service';

@Injectable({
  providedIn: 'root',
})
export class LeaguesBLL {
  //#region Loaders Observables

  private isLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  );
  readonly isLoading$: Observable<boolean> = this.isLoading.asObservable();

  private isAddLeagueModelOpen: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  readonly isAddLeagueModelOpen$: Observable<boolean> =
    this.isAddLeagueModelOpen.asObservable();

  private isUpdateLeagueModelOpen: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  readonly isUpdateLeagueModelOpen$: Observable<boolean> =
    this.isUpdateLeagueModelOpen.asObservable();


  private leagues: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  readonly leagues$: Observable<any> = this.leagues.asObservable();

  private schools: BehaviorSubject<any> = new BehaviorSubject<any>([]);
  readonly schools$: Observable<any> = this.schools.asObservable();

  private ad_contacts: BehaviorSubject<any> = new BehaviorSubject<any>([]);
  readonly ad_contacts$: Observable<any> = this.ad_contacts.asObservable();

  private league: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  readonly league$: Observable<any> = this.league.asObservable();

  private schedulerData: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  readonly schedulerData$: Observable<any> = this.schedulerData.asObservable();

  private schedulerFixtures: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  readonly schedulerFixtures$: Observable<any> = this.schedulerFixtures.asObservable();

  private creation_status: BehaviorSubject<string> =
    new BehaviorSubject<string>(null);
  readonly creation_status$: Observable<string> =
    this.creation_status.asObservable();

  selectedLeague$: Subject<any> = new Subject<any>();

  public reloadAIFixturesEvent: EventEmitter<boolean> = new EventEmitter<boolean>(false);

  //#endregion Data Observables

  constructor(
    private api: ApiService,
    private toast: ToastService,
    private router: Router,
    private auth: AuthService
  ) { }

  // Wait for authenticated user to ensure auth token is available
  private waitForAuthenticatedUser(): Observable<any> {
    return this.auth.auth$.pipe(
      skipWhile((val) => !val?.id),
      take(1)
    );
  }

  reloadAIFixtures() {
    this.isLoading.next(true);
    this.reloadAIFixturesEvent.emit(true);
    // Logic to reload AI fixtures
  }

  turnLoadingOff() {
    this.isLoading.next(false);
  }

  public getLeagues(admin_id: string = null) {
    this.isLoading.next(true);

    let url = `league?type=get_all`;

    if (admin_id) {
      url += `&admin_id=${admin_id}`;
    }

    this.api
      .get(url)
      .pipe(take(1))
      .subscribe(
        (response: any) => {
          this.leagues.next(response);
          this.isLoading.next(false);
        },
        (err) => {
          this.isLoading.next(false);
          console.error('Error fetching leagues:', err);
        }
      );
  }

  public getLeagueById(leagueId: string, admin_id: string = null): Observable<any> {
    this.isLoading.next(true);

    let url = `league?id=${leagueId}`;

    if (admin_id) {
      url += `&admin_id=${admin_id}`;
    }

    return this.api.get(url).pipe(
      take(1),
      tap((response: any) => {
        this.isLoading.next(false);
      }),
      tap(
        null,
        (err) => {
          this.isLoading.next(false);
          console.error('Error fetching league:', err);
        }
      )
    );
  }

  public getLeagueSchedulerById(leagueId: string, admin_id: string = null): Observable<any> {
    console.log('Fetching league scheduler for league ID:', leagueId);
    if (!leagueId) {
      console.error('Invalid league ID');
      return null;
    }
    this.isLoading.next(true);

    let url = `league?id=${leagueId}&type=get_league_scheduler_by_id`;

    if (admin_id) {
      url += `&admin_id=${admin_id}`;
    }

    return this.api.get(url).pipe(
      take(1),
      tap((response: any) => {
        this.isLoading.next(false);
      }),
      tap(
        null,
        (err) => {
          this.isLoading.next(false);
          console.error('Error fetching league scheduler:', err);
        }
      )
    );
  }

  public getAllLeagueScheduler(leagueId: string, admin_id: string = null, sport_id: string = null) {
    // Clear cached data to avoid showing stale data
    this.schedulerData.next(null);
    this.isLoading.next(true);

    let url = `league?type=get_all_league_scheduler&id=${leagueId}`;

    if (admin_id) {
      url += `&admin_id=${admin_id}`;
    }

    if (sport_id) {
      url += `&sport_id=${sport_id}`;
    }

    this.api
      .get(url)
      .pipe(take(1))
      .subscribe(
        (response: any) => {
          this.schedulerData.next(response);
          this.isLoading.next(false);
        },
        (err) => {
          this.isLoading.next(false);
          console.error('Error fetching all league schedulers:', err);
        }
      );
  }

  copyLeagueScheduler(payload) {
    this.isLoading.next(true);

    let url = `league?type=copy_league_scheduler`;

    this.api
      .post(url, payload)
      .pipe(take(1))
      .subscribe(
        (response: any) => {
          console.log("copy league scheduler response", response);
          this.isLoading.next(false);
          this.toast.showToast('Success!', 'League Scheduler has been copied successfully.');
          this.getAllLeagueScheduler(payload.league_id);
        },
        (err) => {
          this.isLoading.next(false);
          console.error('Error fetching all league schedulers:', err);
        }
      );
  }

  public getSchedulerFixtures(schedulerId: string, admin_id: string = null): Observable<any> {
    return new Observable(observer => {
      this.waitForAuthenticatedUser().subscribe(() => {
        this.isLoading.next(true);

        let url = `league?type=get_league_scheduler_by_id&id=${schedulerId}`;

        if (admin_id) {
          url += `&admin_id=${admin_id}`;
        }

        this.api.get(url).pipe(
          take(1),
          tap((response: any) => {
            this.schedulerFixtures.next(response);
            this.isLoading.next(false);
          }),
          tap(
            null,
            (err) => {
              this.isLoading.next(false);
              console.error('Error fetching scheduler data:', err);
            }
          )
        ).subscribe(
          (response) => {
            observer.next(response);
            observer.complete();
          },
          (err) => {
            observer.error(err);
          }
        );
      });
    });
  }

  public getLeague() {
    this.isLoading.next(true);

    let url = `field-events?type=get_league`;

    this.api
      .get(url)
      .pipe(take(1))
      .subscribe(
        (response: any) => {
          this.league.next(response);
          this.isLoading.next(false);
        },
        (err) => {
          this.isLoading.next(false);
        }
      );
  }

  public async createLeague(league, admin_id: string = null) {
    this.isLoading.next(true);

    let url = `field-events?type=post_league`;

    if (admin_id) {
      url += `&admin_id=${admin_id}`;
    }

    this.api
      .post(url, league)
      .pipe(take(1))
      .subscribe(
        (response: any) => {
          this.isLoading.next(false);
          this.creation_status.next('success');
          this.toast.showToast('Success!', 'League has been created.');
          this.toggleAddLeagueModel(false);
        },
        (err) => {
          this.isLoading.next(false);
        }
      );
  }



  public async deleteLeague(id: string, admin_id: string = null) {
    let url = `league?id=${id}&type=delete_league`;

    if (admin_id) {
      url += `&admin_id=${admin_id}`;
    }

    this.api.delete(url).subscribe(
      async () => {
        this.getLeagues(admin_id);
        this.toast.showToast('Success!', 'League has been deleted.');
      },
      async (err) => {

      }
    );
  }

  public async editLeague(id, league, admin_id: string = null) {
    let url = `field-events?type=update_league`;

    if (admin_id) {
      url += `&admin_id=${admin_id}`;
    }

    this.isLoading.next(true);

    let update_league = { ...league, id: id };

    this.api
      .patch(url, update_league)
      .pipe(take(1))
      .subscribe(
        (response: any) => {
          this.isLoading.next(false);
          this.toast.showToast('Success!', 'League has been updated.');
          this.creation_status.next('success');
        },
        (err) => {
          this.isLoading.next(false);
        }
      );
  }



  toggleAddLeagueModel(isOpen: boolean) {
    if (isOpen) {
      this.get_schools();
    }
    this.isAddLeagueModelOpen.next(isOpen);
  }

  changeUpdateLeagueModelOpen(isOpen: boolean) {
    this.isUpdateLeagueModelOpen.next(isOpen);
  }

  get_schools(admin_id: string = null) {
    this.isLoading.next(true);
    let url = `field-events`;

    if (admin_id) {
      url += `?admin_id=${admin_id}`;
    }

    this.api.get(url).pipe(take(1)).subscribe((res: any) => {
      this.isLoading.next(false);
      this.schools.next(res.my_clubs);
      this.ad_contacts.next(res.ad_contacts);
    }, (err) => {
      this.isLoading.next(false);
    });
  }

  // Step 1 API - League Basic Info
  public async createLeagueStep1(league, admin_id: string = null): Promise<void> {
    return new Promise((resolve, reject) => {
      this.isLoading.next(true);

      let url = `field-events?type=post_league_step1`;

      if (admin_id) {
        url += `&admin_id=${admin_id}`;
      }

      this.api
        .post(url, league)
        .pipe(take(1))
        .subscribe(
          (response: any) => {
            this.isLoading.next(false);
            this.toast.showToast('Success!', 'Step 1 completed successfully.');
            resolve();
          },
          (err) => {
            this.isLoading.next(false);
            this.toast.showToast('Error!', 'Failed to save step 1.', 'danger');
            reject(err);
          }
        );
    });
  }

  // Step 2 API
  public async createLeagueStep2(data, admin_id: string = null): Promise<void> {
    return new Promise((resolve, reject) => {
      this.isLoading.next(true);

      let url = `field-events?type=post_league_step2`;

      if (admin_id) {
        url += `&admin_id=${admin_id}`;
      }

      this.api
        .post(url, data)
        .pipe(take(1))
        .subscribe(
          (response: any) => {
            this.isLoading.next(false);

            // Store league ID in localStorage for later use
            if (response && response.league_id) {
              localStorage.setItem('current_league_id', response.league_id);
            }

            this.toast.showToast('Success!', 'Step 2 completed successfully.');
            resolve();
          },
          (err) => {
            this.isLoading.next(false);
            this.toast.showToast('Error!', 'Failed to save step 2.', 'danger');
            reject(err);
          }
        );
    });
  }

  // Step 3 API
  public async createLeagueStep3(data, admin_id: string = null): Promise<void> {
    return new Promise((resolve, reject) => {
      this.isLoading.next(true);

      let url = `field-events?type=post_league_step3`;

      if (admin_id) {
        url += `&admin_id=${admin_id}`;
      }

      this.api
        .post(url, data)
        .pipe(take(1))
        .subscribe(
          (response: any) => {
            this.isLoading.next(false);
            this.toast.showToast('Success!', 'Step 3 completed successfully.');
            resolve();
          },
          (err) => {
            this.isLoading.next(false);
            this.toast.showToast('Error!', 'Failed to save step 3.', 'danger');
            reject(err);
          }
        );
    });
  }

  // Step 4 API
  public async createLeagueStep4(data, admin_id: string = null): Promise<void> {
    return new Promise((resolve, reject) => {
      this.isLoading.next(true);

      let url = `field-events?type=post_league_step4`;

      if (admin_id) {
        url += `&admin_id=${admin_id}`;
      }

      this.api
        .post(url, data)
        .pipe(take(1))
        .subscribe(
          (response: any) => {
            this.isLoading.next(false);
            this.toast.showToast('Success!', 'Step 4 completed successfully.');
            resolve();
          },
          (err) => {
            this.isLoading.next(false);
            this.toast.showToast('Error!', 'Failed to save step 4.', 'danger');
            reject(err);
          }
        );
    });
  }

  // Step 5 API
  public async createLeagueStep5(data, admin_id: string = null): Promise<void> {
    return new Promise((resolve, reject) => {
      this.isLoading.next(true);

      let url = `field-events?type=post_league_step5`;

      if (admin_id) {
        url += `&admin_id=${admin_id}`;
      }

      this.api
        .post(url, data)
        .pipe(take(1))
        .subscribe(
          (response: any) => {
            this.isLoading.next(false);
            this.toast.showToast('Success!', 'League creation completed successfully.');
            this.creation_status.next('success');
            this.toggleAddLeagueModel(false);
            resolve();
          },
          (err) => {
            this.isLoading.next(false);
            this.toast.showToast('Error!', 'Failed to save step 5.', 'danger');
            reject(err);
          }
        );
    });
  }

  // New League API - POST to /league endpoint
  public async createLeagueNew(leagueData: any, admin_id: string = null): Promise<void> {
    return new Promise((resolve, reject) => {
      this.isLoading.next(true);

      let url = `league`;

      // Format the data according to API requirements
      const formattedData = {
        name: leagueData.name,
        competition_year: leagueData.competition_year,
        season: leagueData.season,
        admins: leagueData.admins || [],
        schools: leagueData.schools || []
      };

      this.api
        .post(url, formattedData)
        .pipe(take(1))
        .subscribe(
          (response: any) => {
            this.isLoading.next(false);
            this.toast.showToast('Success!', 'League has been created successfully.');
            this.creation_status.next('success');
            resolve();
          },
          (err) => {
            this.isLoading.next(false);
            this.toast.showToast('Error!', 'Failed to create league.', 'danger');
            reject(err);
          }
        );
    });
  }

  // Update League Basic Info API
  public async updateLeague(leagueId: string, leagueData: any, admin_id: string = null): Promise<void> {
    return new Promise((resolve, reject) => {
      this.isLoading.next(true);

      let url = `league?id=${leagueId}`;

      if (admin_id) {
        url += `&admin_id=${admin_id}`;
      }

      // Format the data according to API requirements
      const formattedData: any = {
        id: leagueId,
        name: leagueData.name,
        competition_year: leagueData.competition_year,
        season: leagueData.season
      };

      // Include schools array only if new schools are being added
      if (leagueData.schools && Array.isArray(leagueData.schools) && leagueData.schools.length > 0) {
        formattedData.schools = leagueData.schools;
      }

      // Include admins array only if new admins are being added
      if (leagueData.admins && Array.isArray(leagueData.admins) && leagueData.admins.length > 0) {
        formattedData.admins = leagueData.admins;
      }

      this.api
        .patch(url, formattedData)
        .pipe(take(1))
        .subscribe(
          (response: any) => {
            this.isLoading.next(false);
            this.toast.showToast('Success!', 'League has been updated successfully.');
            this.creation_status.next('success');
            resolve();
          },
          (err) => {
            this.isLoading.next(false);
            this.toast.showToast('Error!', 'Failed to update league.', 'danger');
            reject(err);
          }
        );
    });
  }

  // Archive/Unarchive League API
  public async toggleArchiveLeague(leagueId: string, isArchived: boolean, admin_id: string = null): Promise<void> {
    return new Promise((resolve, reject) => {
      this.isLoading.next(true);

      let url = `league?type=archive&id=${leagueId}`;

      // if (admin_id) {
      //   url += `&admin_id=${admin_id}`;
      // }

      const payload = {
        is_archived: isArchived
      };

      this.api
        .patch(url, payload)
        .pipe(take(1))
        .subscribe(
          (response: any) => {
            this.isLoading.next(false);
            this.toast.showToast('Success!', `League has been ${isArchived ? 'archived' : 'activated'} successfully.`);
            // Refresh the leagues list
            this.getLeagues(admin_id);
            resolve();
          },
          (err) => {
            this.isLoading.next(false);
            this.toast.showToast('Error!', 'Failed to update archive status.', 'danger');
            reject(err);
          }
        );
    });
  }

  // Update School Admin API
  // schoolRecordId: The record ID from schools array (for query parameter)
  // schoolId: The actual school_id (for payload)
  public async updateSchoolAdmin(schoolRecordId: string, leagueId: string, schoolId: string, assignedAdId: string, admin_id: string = null): Promise<void> {
    return new Promise((resolve, reject) => {
      this.isLoading.next(true);

      let url = `league?type=update_school&id=${schoolRecordId}`;

      if (admin_id) {
        url += `&admin_id=${admin_id}`;
      }

      const payload = {
        league_id: leagueId,
        school_id: schoolId,
        assigned_ad_id: assignedAdId
      };

      this.api
        .patch(url, payload)
        .pipe(take(1))
        .subscribe(
          (response: any) => {
            this.isLoading.next(false);
            this.toast.showToast('Success!', 'School admin has been updated successfully.');
            resolve();
          },
          (err) => {
            this.isLoading.next(false);
            this.toast.showToast('Error!', 'Failed to update school admin.', 'danger');
            reject(err);
          }
        );
    });
  }

  // Update Sport Admin API
  public async updateSportAdmin(adminId: string, leagueId: string, sportsId: string, assignedAdId: string, assignedGenders: string = '', assignedLevels: string = '', admin_id: string = null): Promise<void> {
    return new Promise((resolve, reject) => {
      this.isLoading.next(true);

      let url = `league?type=update_admin&id=${adminId}`;

      if (admin_id) {
        url += `&admin_id=${admin_id}`;
      }

      const payload = {
        league_id: leagueId,
        sports_id: sportsId,
        assigned_ad_id: assignedAdId,
        assigned_genders: assignedGenders,
        assigned_levels: assignedLevels
      };

      this.api
        .patch(url, payload)
        .pipe(take(1))
        .subscribe(
          (response: any) => {
            this.isLoading.next(false);
            this.toast.showToast('Success!', 'Sport admin has been updated successfully.');
            resolve();
          },
          (err) => {
            this.isLoading.next(false);
            this.toast.showToast('Error!', 'Failed to update sport admin.', 'danger');
            reject(err);
          }
        );
    });
  }

  // Update League Scheduler API
  public async updateLeagueScheduler(schedulerData: any, admin_id: string = null): Promise<void> {
    return new Promise((resolve, reject) => {
      this.isLoading.next(true);

      let url = `league?type=update_league_scheduler`;

      if (admin_id) {
        url += `&admin_id=${admin_id}`;
      }

      // Format the payload according to API requirements
      const payload = {
        id: schedulerData.id, // scheduler UUID (required)
        gender: schedulerData.gender,
        versity_club: schedulerData.versity_club,
        league_id: schedulerData.league_id,
        conference_needed: schedulerData.conference_needed,
        start_date: schedulerData.start_date, // Format: ISO 8601 with timezone (e.g., "2025-11-27T11:55:36.804Z")
        end_date: schedulerData.end_date, // Format: ISO 8601 with timezone (e.g., "2025-11-27T11:55:36.804Z")
        is_bye_week_enabled: schedulerData.is_bye_week_enabled,
        bye_week_start_date: schedulerData.bye_week_start_date,
        how_many_bye_week: schedulerData.how_many_bye_week,
        max_byes_per_team: schedulerData.max_byes_per_team,
        teams_on_bye_per_week: schedulerData.teams_on_bye_per_week,
        is_days_of_week_enabled: schedulerData.is_days_of_week_enabled,
        set_days_of_weeks: schedulerData.set_days_of_weeks, // Array of day names
        is_home_away_equal: schedulerData.is_home_away_equal,
        are_teams_playing_multiple: schedulerData.are_teams_playing_multiple,
        number_of_game_playing: schedulerData.number_of_game_playing,
        holidays: schedulerData.holidays || [], // Array of { date_of_holiday: ISO 8601 format }
        groups_enabled: schedulerData.groups_enabled,
        number_of_groups: schedulerData.number_of_groups,
        intra_group_play: schedulerData.intra_group_play,
        intra_group_matches: schedulerData.intra_group_matches,
        inter_group_play: schedulerData.inter_group_play,
        inter_group2_play: schedulerData.inter_group2_play,
        inter_group_matches: schedulerData.inter_group_matches
      };



      this.api
        .patch(url, payload)
        .pipe(take(1))
        .subscribe(
          (response: any) => {
            this.isLoading.next(false);
            this.toast.showToast('Success!', 'League scheduler has been updated successfully.');
            resolve();
          },
          (err) => {
            this.isLoading.next(false);
            this.toast.showToast('Error!', 'Failed to update league scheduler.', 'danger');
            reject(err);
          }
        );
    });
  }

  // Delete League Scheduler API
  public async deleteLeagueScheduler(schedulerId: string, leagueId: string, admin_id: string = null): Promise<void> {
    return new Promise((resolve, reject) => {
      this.isLoading.next(true);

      let url = `league?type=delete_league_scheduler&id=${schedulerId}`;

      if (admin_id) {
        url += `&admin_id=${admin_id}`;
      }

      this.api
        .delete(url)
        .pipe(take(1))
        .subscribe(
          (response: any) => {
            this.isLoading.next(false);
            this.toast.showToast('Success!', 'Scheduler has been deleted successfully.');
            // Refresh the scheduler list
            this.getAllLeagueScheduler(leagueId, admin_id);
            resolve();
          },
          (err) => {
            this.isLoading.next(false);
            this.toast.showToast('Error!', 'Failed to delete scheduler.', 'danger');
            reject(err);
          }
        );
    });
  }

}

